<template>
  <transition name="fade">
    <div class="modal" v-if="showPrivacyPolicy">
      <div class="modal-header">
        <h2>Privacy Policy</h2>
        <span class="close-modal" @click="close">✕</span>
      </div>
      <div class="modal-body">
        <div class="privacy-content">
          <div class="app-name">Truth or Juicy Truth</div>
          <div class="last-updated">Last updated: October 9, 2025</div>

          <p>Welcome to Truth or Juicy Truth! Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your information when you use our app.</p>

          <h3>1. Information We Collect</h3>
          
          <h4>1.1 Information You Provide</h4>
          <ul>
            <li><strong>Question Suggestions:</strong> When you submit question suggestions, we collect the content of your submission.</li>
            <li><strong>Feedback:</strong> Any feedback or communications you send to us.</li>
            <li><strong>User Preferences:</strong> Settings such as whether you've enabled adult questions and dark mode preferences.</li>
          </ul>

          <h4>1.2 Automatically Collected Information</h4>
          <ul>
            <li><strong>Usage Data:</strong> We collect anonymous analytics data about how you interact with the app (e.g., which features you use, button clicks, session duration).</li>
            <li><strong>Device Information:</strong> Basic device information such as device type, operating system, and app version.</li>
            <li><strong>Technical Data:</strong> Error logs and performance data to help us improve the app.</li>
          </ul>

          <h3>2. How We Use Your Information</h3>
          
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide and improve the app's functionality</li>
            <li>Review and potentially add user-submitted questions to our database</li>
            <li>Understand how users interact with the app to enhance user experience</li>
            <li>Fix bugs and technical issues</li>
            <li>Respond to your feedback and support requests</li>
            <li>Analyze app performance and user engagement</li>
          </ul>

          <h3>3. Data Storage and Security</h3>
          
          <p>We take the security of your data seriously:</p>
          <ul>
            <li>Your data is stored securely using industry-standard encryption</li>
            <li>We use secure third-party services (Supabase, PostHog) for data storage and analytics</li>
            <li>We implement appropriate technical and organizational measures to protect your information</li>
            <li>Most data collected is anonymous and cannot be linked to individual users</li>
          </ul>

          <h3>4. Third-Party Services</h3>
          
          <p>We use the following third-party services:</p>
          <ul>
            <li><strong>PostHog:</strong> For analytics and understanding user behavior. PostHog processes usage data according to their privacy policy.</li>
            <li><strong>Supabase:</strong> For secure data storage of suggestions and feedback. Supabase processes data according to their privacy policy.</li>
          </ul>
          
          <p>These services may collect and process data as described in their respective privacy policies. We recommend reviewing:</p>
          <ul>
            <li>PostHog Privacy Policy: <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">posthog.com/privacy</a></li>
            <li>Supabase Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a></li>
          </ul>

          <h3>5. Data Retention</h3>
          
          <p>We retain your information only for as long as necessary to fulfill the purposes outlined in this policy. Anonymous analytics data may be retained indefinitely for statistical purposes. User submissions (suggestions and feedback) are retained for review and potential use in the app.</p>

          <h3>6. Your Rights</h3>
          
          <p>You have the right to:</p>
          <ul>
            <li>Request access to your personal data</li>
            <li>Request correction of your personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Opt-out of analytics tracking</li>
            <li>Withdraw consent at any time</li>
          </ul>

          <h3>7. Children's Privacy</h3>
          
          <p>Truth or Juicy Truth is intended for users aged 17 and older. We do not knowingly collect personal information from children under 17. If you believe we have collected information from a child under 17, please contact us immediately.</p>

          <h3>8. Changes to This Policy</h3>
          
          <p>We may update this Privacy Policy from time to time. We will notify users of any significant changes by updating the "Last updated" date at the top of this policy. Continued use of the app after changes constitutes acceptance of the updated policy.</p>

          <h3>9. International Users</h3>
          
          <p>If you are accessing our app from outside the United States, please note that your information may be transferred to, stored, and processed in the United States or other countries where our service providers operate. By using the app, you consent to this transfer.</p>

          <h3>10. Contact Us</h3>
          
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at:</p>
          <p style="text-align: center; margin-top: 20px;">
            <a href="mailto:contact@truthorjuicy.com" class="contact-email">contact@truthorjuicy.com</a>
          </p>

          <h3>11. Consent</h3>
          
          <p>By using Truth or Juicy Truth, you consent to the collection and use of your information as described in this Privacy Policy.</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { useStore } from '../store';
import { mapState } from 'pinia';

export default {
  computed: {
    ...mapState(useStore, ['showPrivacyPolicy']),
  },
  methods: {
    close() {
      useStore().showPrivacyPolicy = false;
    },
  },
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 80%;
    max-width: 600px;
    max-height: 90vh;
    background: white;
    transform: translate(-50%, -50%);
    padding: 20px;
    z-index: 15000;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border-radius: 15px;
    transition: background-color 0.3s ease, color 0.3s ease;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-shrink: 0;
}

.modal-header h2 {
    margin: 0;
    color: var(--raspberry-pink);
    transition: color 0.3s ease;
}

.close-modal {
    cursor: pointer;
    font-size: 24px;
    color: #666;
    transition: color 0.3s ease;
}

.close-modal:hover {
    color: #333;
}

.modal-body {
    margin-top: 10px;
    color: #333;
    transition: color 0.3s ease;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

body.dark-mode .modal {
    background-color: var(--background-dark);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
}

body.dark-mode .modal-header h2 {
    color: var(--peach);
}

body.dark-mode .close-modal {
    color: #999;
}

body.dark-mode .close-modal:hover {
    color: var(--text-dark);
}

body.dark-mode .modal-body {
    color: var(--text-dark);
}

.privacy-content {
  max-height: calc(90vh - 100px);
  overflow-y: auto;
  padding-right: 10px;
}

.app-name {
  text-align: center;
  font-size: 1.2rem;
  color: var(--coral);
  margin-bottom: 10px;
  font-weight: bold;
}

.last-updated {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  font-style: italic;
}

.privacy-content h3 {
  color: var(--coral);
  margin: 1.5em 0 0.5em;
  font-size: 1.3em;
}

.privacy-content h4 {
  color: var(--raspberry-pink);
  margin: 1em 0 0.5em;
  font-size: 1.1em;
}

.privacy-content p {
  margin: 0.8em 0;
  line-height: 1.5;
}

.privacy-content ul {
  margin: 0.8em 0;
  padding-left: 1.5em;
}

.privacy-content li {
  margin: 0.5em 0;
  line-height: 1.5;
}

.privacy-content a {
  color: var(--coral);
  text-decoration: none;
  font-weight: bold;
}

.privacy-content a:hover {
  text-decoration: underline;
}

.contact-email {
  color: var(--coral);
  text-decoration: none;
  font-weight: bold;
}

.contact-email:hover {
  text-decoration: underline;
}

body.dark-mode .privacy-content h3 {
  color: var(--coral);
}

body.dark-mode .privacy-content h4 {
  color: var(--peach);
}

body.dark-mode .app-name {
  color: var(--coral);
}

body.dark-mode .last-updated {
  color: #999;
}

.privacy-content::-webkit-scrollbar {
  width: 8px;
}

.privacy-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.privacy-content::-webkit-scrollbar-thumb {
  background: var(--raspberry-pink);
  border-radius: 4px;
}

body.dark-mode .privacy-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-mode .privacy-content::-webkit-scrollbar-thumb {
  background: var(--peach);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>

